// Wave 13 / Track F — Cp(θ)-weighted area share per body region.
//
// PressureMesh visualizes Cp(θ) = cos(θ) on the cylinder primitives but
// the analytical drag attribution in sectional.py uses a *different*
// model (Hoerner cylinder Cd × shielding LUT). The numbers can disagree.
//
// Track F first shipped an analytical closed-form integration. Track F
// finish replaces it with a discrete per-vertex integration over the
// SAME cylinder + sphere discretization PressureMesh paints — so the
// drag math sums the literal Cp values shown on the visualization.
//
// For each body part we:
//   1. Discretize the surface (cylinders: N_radial × N_axial; spheres:
//      N_lat × N_lon).
//   2. Compute the outward normal at each sample.
//   3. Cp(vertex) = max(0, cos(normal · windWorld)) — only front-facing
//      surface contributes to drag in this analytical model.
//   4. Weight by the vertex's surface-area element (uniform across each
//      part).
//   5. Sum per region, then normalize so the four regions sum to 1.0.

import * as THREE from 'three';
import { BodyPartFrame, BodyRegion } from './cylinderBody';

export interface PressureWeightedShares {
  head: number;
  torso: number;
  arms: number;
  legs: number;
}

const CYL_RADIAL = 24;
const CYL_AXIAL = 6;
const SPH_LAT = 12;
const SPH_LON = 24;

/** Integrate Cp(θ) over the cylinder lateral surface as Σ max(0, n·w) ΔA,
 *  where n is the outward radial normal at each (i, j) and ΔA is the
 *  uniform area element. Returns the integrated front-facing Cp-weighted
 *  area in m². */
function cylinderCpAreaPerVertex(
  part: BodyPartFrame,
  windWorld: THREE.Vector3,
): number {
  // Build an orthonormal basis (u, v) perpendicular to the cylinder axis.
  const axis = part.axis.clone().normalize();
  const ref = Math.abs(axis.y) < 0.9
    ? new THREE.Vector3(0, 1, 0)
    : new THREE.Vector3(1, 0, 0);
  const u = new THREE.Vector3().crossVectors(ref, axis).normalize();
  const v = new THREE.Vector3().crossVectors(axis, u).normalize();
  const dA = (2 * Math.PI * part.radius * part.length) / (CYL_RADIAL * CYL_AXIAL);
  let sum = 0;
  for (let j = 0; j < CYL_AXIAL; j++) {
    for (let i = 0; i < CYL_RADIAL; i++) {
      const theta = (i / CYL_RADIAL) * Math.PI * 2;
      // Outward radial normal in world coords.
      const nx = Math.cos(theta);
      const ny = Math.sin(theta);
      // n = u*nx + v*ny (axial component is zero for the lateral surface).
      const normWorld = new THREE.Vector3()
        .copy(u).multiplyScalar(nx)
        .addScaledVector(v, ny);
      const cp = -normWorld.dot(windWorld);
      // cp > 0 means this vertex faces the wind (drag-contributing).
      if (cp > 0) sum += cp * dA;
    }
  }
  return sum;
}

/** Same idea for the head sphere. Outward normal at a sample point is
 *  simply (position - center) / r, which for a unit-sphere lat/lon
 *  parameterization becomes a closed form. */
function sphereCpAreaPerVertex(
  part: BodyPartFrame,
  windWorld: THREE.Vector3,
): number {
  const r = part.radius;
  // dA at sample (lat_i, lon_j) on a unit sphere = sin(lat) dlat dlon × r².
  const dlat = Math.PI / SPH_LAT;
  const dlon = (2 * Math.PI) / SPH_LON;
  let sum = 0;
  for (let i = 0; i < SPH_LAT; i++) {
    const lat = (i + 0.5) * dlat; // 0..π
    const sinLat = Math.sin(lat);
    const cosLat = Math.cos(lat);
    for (let j = 0; j < SPH_LON; j++) {
      const lon = (j + 0.5) * dlon;
      // Outward normal = (sin lat cos lon, cos lat, sin lat sin lon)
      // in part-local coords (Y up). We don't apply part.axis rotation
      // for the sphere — it's the head and the head doesn't have a
      // preferred axial direction in this model.
      const nx = sinLat * Math.cos(lon);
      const ny = cosLat;
      const nz = sinLat * Math.sin(lon);
      const cp = -(nx * windWorld.x + ny * windWorld.y + nz * windWorld.z);
      if (cp > 0) {
        const dA = r * r * sinLat * dlat * dlon;
        sum += cp * dA;
      }
    }
  }
  return sum;
}

/** Per-region Cp-weighted area shares (sum = 1.0). Returns null when the
 *  body frames list is empty.
 *  Wave 13 / Track F finish — per-vertex integration over the actual
 *  cylinder + sphere discretization. */
export function computePressureWeightedShares(
  parts: BodyPartFrame[],
  windWorld: THREE.Vector3,
): PressureWeightedShares | null {
  if (!parts || parts.length === 0) return null;
  const raw: PressureWeightedShares = { head: 0, torso: 0, arms: 0, legs: 0 };
  for (const part of parts) {
    if (part.visibility < 0.4) continue;
    const a =
      part.kind === 'sphere'
        ? sphereCpAreaPerVertex(part, windWorld)
        : cylinderCpAreaPerVertex(part, windWorld);
    raw[part.region as BodyRegion] += a;
  }
  const total = raw.head + raw.torso + raw.arms + raw.legs;
  if (total <= 0) return null;
  return {
    head: raw.head / total,
    torso: raw.torso / total,
    arms: raw.arms / total,
    legs: raw.legs / total,
  };
}
